/* eslint-disable @typescript-eslint/no-unused-vars */
import { ArrowLeft20 } from '@carbon/icons-react';
import React, { useMemo, useState } from 'react';
import ReactPlayer from 'react-player';
import styled from 'styled-components';
import { Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import {
  CpHeading03Primary,
  CpBody03Secondary,
  CpBody01Secondary,
  CpBody03Primary,
  CpBody03Placeholder,
} from 'components/atoms/customer-portal/typography';
import { useGetBackground } from 'components/organisms/customer-portal/types/utils';
import { useIsResponsive } from 'hooks/use-media-query';
import { CpSectionWrapper } from '../../common';
import {
  ControlButton,
  RightArrow,
} from '../../image-gallery/variants/image-gallery-carousel';
import { CpVideosVariantWidgetProps } from '../types';

export const CpVideoCarouselWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  align-items: flex-start;
  overflow-y: hidden;
  gap: var(--cp-spacing-06);

  .swiper {
    width: 85%;
    height: 50%;
    margin: 0 var(--cp-spacing-06);
    margin-top: var(--cp-spacing-08);

    @media (max-width: 768px) {
      width: 80%;
    }

    @media (max-width: 480px) {
      width: 100%;
      margin: 0;
    }
  }

  .swiper-slide {
    text-align: center;
    font-size: 18px;

    /* Center slide text vertically */
    display: flex;
    justify-content: center;
    align-items: center;
    height: auto;
  }

  .swiper-slide img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
  }

  .swiper-pagination-bullets {
    margin-top: var(--cp-spacing-06);
    display: flex;
    justify-content: center;
    gap: var(--cp-spacing-03);

    @media (max-width: 480px) {
      justify-content: flex-start;
      align-items: flex-start;
      margin-top: var(--cp-spacing-09);
    }
  }

  .swiper-pagination-bullet {
    display: block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: var(--cp-background-as-black-opacity);
    cursor: pointer !important;
  }

  .swiper-pagination-bullet-active {
    background-color: black;
  }
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-03);
`;

const Title = styled(CpHeading03Primary)`
  margin-bottom: 0;
`;

const Description = styled(CpBody03Placeholder)`
  margin-bottom: 0;
`;

export const VideoCarouselVideosGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-gap: var(--cp-spacing-05);
  width: 100%;
  /* padding-top: 40px; */

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    /* grid-gap: 0; */
  }
`;

export const VideoCarouselVideoCard = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: var(--cp-spacing-06);
  padding: var(--cp-spacing-06);
  border: 1px solid var(--Border-border-subtle, rgba(0, 0, 0, 0.12));
  background-color: white;
`;

const VideoTitle = styled(CpBody03Primary)`
  margin-bottom: 0;
`;

const VideoDescription = styled(CpBody01Secondary)`
  margin-bottom: 0;
`;

const ControlsAtBottom = styled.div`
  gap: 24px;
  position: absolute;
  display: flex;
  left: 14rem;
  top: 38.5rem;
  z-index: 10;
`;

export const CpVideoGrid: React.FC<CpVideosVariantWidgetProps> = ({
  disabled,
  data,
  widget,
}) => {
  const background = useGetBackground(widget);
  const isResponsive = useIsResponsive();

  const [current, setCurrent] = useState(0);
  const [swiperRef, setSwiperRef] = useState<any>();

  const handlePrevious1 = () => {
    let slideTo;

    if (current - 1 === -1) {
      slideTo = data?.resources.length - 1;
    } else {
      slideTo = current - 1;
    }

    setCurrent((val) => slideTo);

    (swiperRef as any)?.slideTo(slideTo);
  };

  const handleNext1 = () => {
    let slideTo;

    if (current + 1 === data?.resources.length) {
      slideTo = 0;
    } else {
      slideTo = current + 1;
    }

    setCurrent((val) => slideTo);
    (swiperRef as any)?.slideTo(slideTo);
  };

  const resources = useMemo(
    () =>
      data?.resources?.filter((e) => {
        return (
          !!e?.imageLink && !!e?.name?.trim?.()
        );
      }),
    [data?.resources],
  );

  return (
    <CpSectionWrapper
      paddingY={widget?.widgetDefinition?.styles?.paddingY as number}
      isDisabled={disabled}
      background={background}
      data-builder-mode-full-tile="true"
    >
      <CpVideoCarouselWrapper>
        <TitleWrapper>
          <Title autoAdjustColor data-cy="cp.videos_list_grid.title">
            {data.title ?? 'Videos'}
          </Title>
          <Description
            autoAdjustColor
            data-cy="cp.videos_list_grid.description"
          >
            {data.description}
          </Description>
        </TitleWrapper>
        {isResponsive ? (
          <>
            <Swiper
              onSwiper={setSwiperRef}
              modules={[Pagination]}
              pagination={{ clickable: true }}
              className="mySwiper"
              speed={500}
              spaceBetween={50}
              autoplay={{
                delay: 2000,
                disableOnInteraction: true,
              }}
            >
              {resources?.map((metric, index) => (
                <SwiperSlide>
                  <VideoCarouselVideoCard>
                    <ReactPlayer
                      url={metric.imageLink}
                      width="100%"
                      height="192px"
                    />
                    <VideoTitle
                      data-cy={`cp.videos_list_grid.video_${index}.title`}
                    >
                      {metric.name}
                    </VideoTitle>
                    <VideoDescription
                      data-cy={`cp.videos_list_grid.video_${index}.description`}
                    >
                      {metric.description}
                    </VideoDescription>
                  </VideoCarouselVideoCard>
                </SwiperSlide>
              ))}
            </Swiper>
            <ControlsAtBottom>
              <ControlButton
                data-cy="cp.videos_list_grid.previous"
                className="controlButtonMinimized"
                icon={<ArrowLeft20 className="cp-svg-auto-adjust-fill" />}
                onClick={handlePrevious1}
              />
              <ControlButton
                data-cy="cp.videos_list_grid.next"
                className="controlButtonMinimized"
                icon={<RightArrow className="cp-svg-auto-adjust-fill" />}
                onClick={handleNext1}
              />
            </ControlsAtBottom>
          </>
        ) : (
          <VideoCarouselVideosGrid>
            {resources?.map((metric, index) => (
              <VideoCarouselVideoCard
                data-cy={`cp.videos_list_grid.video_${index}_card`}
              >
                <ReactPlayer
                  data-cy={`cp.videos_list_grid.video_${index}_player`}
                  url={metric.imageLink}
                  width="100%"
                  height="192px"
                  controls
                />
                <VideoTitle
                  data-cy={`cp.videos_list_grid.video_${index}_title`}
                >
                  {metric.name}
                </VideoTitle>
                <VideoDescription
                  data-cy={`cp.videos_list_grid.video_${index}_description`}
                >
                  {metric.description}
                </VideoDescription>
              </VideoCarouselVideoCard>
            ))}
          </VideoCarouselVideosGrid>
        )}
      </CpVideoCarouselWrapper>
    </CpSectionWrapper>
  );
};
